import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { toast as internalToast } from "@/hooks/use-toast";

/**
 * Centralized toast notification system.
 *
 * This is the ONLY public API for showing toast notifications in the app.
 * All toast calls should use this hook's semantic methods.
 *
 * ## Categories
 *
 * | Method    | Use case                                      | Variant      |
 * |-----------|-----------------------------------------------|--------------|
 * | success   | Completed actions                             | default      |
 * | error     | Failures requiring attention                  | destructive  |
 * | warning   | Partial success or caveats                    | default      |
 * | info      | Neutral information                           | default      |
 *
 * ## Convenience Methods
 *
 * | Method       | Translates to                   |
 * |--------------|---------------------------------|
 * | saved()      | success with "Saved" title      |
 * | created()    | success with "Created" title    |
 * | updated()    | success with "Updated" title    |
 * | deleted()    | success with "Deleted" title    |
 * | copied()     | success with "Copied" title     |
 * | genericError | error with unknown error msg    |
 *
 * ## Usage Examples
 *
 * ```tsx
 * import { useToastNotification } from "@/hooks/useToastNotification";
 *
 * function MyComponent() {
 *   const { success, error, warning, info, saved, copied, genericError } = useToastNotification();
 *
 *   // ─── Core methods ───────────────────────────────────────────
 *
 *   // Success toast with title and optional description
 *   success({ title: "Profile updated", description: "Your changes have been saved." });
 *
 *   // Error toast with explicit title/description
 *   error({ title: "Upload failed", description: "The file exceeds the size limit." });
 *
 *   // Error toast from a caught error (auto-extracts message)
 *   try { await save(); } catch (err) { error(err); }
 *
 *   // Error toast from a string
 *   error("Something went wrong");
 *
 *   // Warning toast (non-destructive, for partial success)
 *   warning({ title: "Imported with issues", description: "3 rows were skipped." });
 *
 *   // Info toast (neutral information)
 *   info({ title: "Tip", description: "You can drag items to reorder." });
 *
 *   // ─── Convenience methods ────────────────────────────────────
 *
 *   saved();                           // "Saved"
 *   saved("Settings applied");         // "Saved" + description
 *   copied();                          // "Copied"
 *   copied("Link copied to clipboard");
 *   genericError();                    // "Error" + "An unknown error occurred"
 * }
 * ```
 */

type ToastOptions = {
  title: string;
  description?: string;
  /**
   * Optional dedupe key. If provided, duplicate toasts with the same key
   * within the TTL window will be suppressed.
   * Recommended format: `${errorType}` - route is auto-prefixed.
   * 
   * For error toasts:
   * - `undefined`: Auto-dedupe from content (baseline protection)
   * - `string`: Use explicit key (platform page pattern)
   * - `null`: Disable deduplication (opt-out for per-item errors)
   */
  dedupeKey?: string | null;
};

type ErrorInput = string | Error | { message?: string } | unknown;

/**
 * Type guard to check if input is a ToastOptions object
 */
function isToastOptions(input: unknown): input is ToastOptions {
  return (
    typeof input === "object" &&
    input !== null &&
    "title" in input &&
    typeof (input as ToastOptions).title === "string"
  );
}

/**
 * Normalizes various error formats into a user-friendly message.
 * Always returns a non-empty string (falls back to translated unknown error).
 */
function normalizeError(error: ErrorInput, fallback: string): string {
  if (typeof error === "string" && error.trim()) {
    return error;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string" &&
    ((error as { message: string }).message).trim()
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
}

export function useToastNotification() {
  const { t } = useTranslation("common");
  const location = useLocation();

  /**
   * Build a dedupe key prefixed with current route
   */
  const buildDedupeKey = (key?: string) => {
    if (!key) return undefined;
    return `${location.pathname}:${key}`;
  };

  /**
   * Show a success toast (default variant, green success feel)
   */
  const success = ({ title, description, dedupeKey }: ToastOptions) => {
    internalToast({
      title,
      description,
      dedupeKey: buildDedupeKey(dedupeKey),
    });
  };

  /**
   * Show an error toast (destructive variant, auto-fallback for empty messages)
   * Supports optional dedupeKey for preventing spam.
   */
  /**
   * Show an error toast (destructive variant, auto-fallback for empty messages)
   * 
   * Deduplication behavior:
   * - No dedupeKey provided: Auto-dedupe from content (baseline protection)
   * - Explicit string key: Use that key (platform page pattern)  
   * - Explicit null: Disable deduplication (opt-out for per-item errors)
   */
  const error = (input: ErrorInput | ToastOptions, dedupeKey?: string | null) => {
    const fallback = t("messages.unknownError");

    // Extract explicit dedupeKey from input if provided
    const explicitKey = dedupeKey ??
      (isToastOptions(input) ? input.dedupeKey : undefined);

    // Determine effective dedupe key:
    // - null: Explicit opt-out, no deduplication
    // - string: Use explicit key (existing behavior)
    // - undefined: Auto-generate from content (baseline safety net)
    let effectiveDedupeKey: string | undefined;

    if (explicitKey === null) {
      // Explicit opt-out - no deduplication
      effectiveDedupeKey = undefined;
    } else if (explicitKey !== undefined) {
      // Explicit key provided - use it (platform page pattern)
      effectiveDedupeKey = buildDedupeKey(explicitKey);
    } else {
      // No explicit key - auto-generate from content (baseline safety net)
      if (isToastOptions(input)) {
        const contentKey = `auto:${input.title}:${input.description || ''}`;
        effectiveDedupeKey = buildDedupeKey(contentKey);
      } else {
        const message = normalizeError(input, fallback);
        const contentKey = `auto:error:${message}`;
        effectiveDedupeKey = buildDedupeKey(contentKey);
      }
    }

    // If it's a ToastOptions object with explicit title
    if (isToastOptions(input)) {
      internalToast({
        title: input.title || fallback,
        description: input.description,
        variant: "destructive",
        dedupeKey: effectiveDedupeKey,
      });
      return;
    }

    // Otherwise treat as error input (string, Error, or unknown)
    const message = normalizeError(input, fallback);
    internalToast({
      title: t("messages.error"),
      description: message,
      variant: "destructive",
      dedupeKey: effectiveDedupeKey,
    });
  };

  /**
   * Show a warning toast (default variant - NOT destructive)
   * Use for partial success or important caveats
   */
  const warning = ({ title, description, dedupeKey }: ToastOptions) => {
    internalToast({
      title,
      description,
      dedupeKey: buildDedupeKey(dedupeKey),
    });
  };

  /**
   * Show an info toast (default variant, neutral information)
   */
  const info = ({ title, description, dedupeKey }: ToastOptions) => {
    internalToast({
      title,
      description,
      dedupeKey: buildDedupeKey(dedupeKey),
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Convenience methods for common operations
  // ─────────────────────────────────────────────────────────────

  /**
   * Quick success toast for save operations
   */
  const saved = (description?: string) => {
    success({
      title: t("messages.saved"),
      description,
    });
  };

  /**
   * Quick success toast for create operations
   */
  const created = (description?: string) => {
    success({
      title: t("created"),
      description,
    });
  };

  /**
   * Quick success toast for update operations
   */
  const updated = (description?: string) => {
    success({
      title: t("messages.updated"),
      description,
    });
  };

  /**
   * Quick success toast for delete operations
   */
  const deleted = (description?: string) => {
    success({
      title: t("messages.deleted"),
      description,
    });
  };

  /**
   * Quick success toast for clipboard operations
   */
  const copied = (description?: string) => {
    success({
      title: t("copied"),
      description,
    });
  };

  /**
   * Generic error toast with standard unknown error message
   */
  const genericError = () => {
    error({
      title: t("messages.error"),
      description: t("messages.unknownError"),
    });
  };

  return {
    // Core semantic methods
    success,
    error,
    warning,
    info,
    // Convenience methods
    saved,
    created,
    updated,
    deleted,
    copied,
    genericError,
  };
}
